import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "../../axiosConfig";
import "../../styles/manageData_Css/ManageDataAdmin.css";

const auth = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
});

const ICONS = {
  countries: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <line x1="2" y1="12" x2="22" y2="12" />
    </svg>
  ),
  grades: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 2 2 7l10 5 10-5-10-5z" />
    </svg>
  ),
  subjects: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20" />
      <path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z" />
    </svg>
  ),
  bookTypes: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
    </svg>
  ),
  standards: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
    </svg>
  ),
  languages: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <circle cx="12" cy="12" r="10" />
      <path d="M2 12h20M12 2a15 15 0 0 1 0 20 15 15 0 0 1 0-20z" />
    </svg>
  ),
  formats: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
    </svg>
  ),
  tags: (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
      <line x1="7" y1="7" x2="7.01" y2="7" />
    </svg>
  ),
};

const TONE_STYLE = {
  primary: { background: "var(--primary-tint)", color: "var(--primary-ink)" },
  mint: { background: "var(--mint-tint)", color: "var(--mint-ink)" },
  peach: { background: "var(--peach-tint)", color: "var(--peach-ink)" },
  lilac: { background: "var(--lilac-tint)", color: "var(--lilac-ink)" },
};

function AddInline({ placeholder, onConfirm, onCancel, extra }) {
  const [val, setVal] = useState("");
  const ref = useRef(null);

  useEffect(() => {
    ref.current?.focus();
  }, []);

  const submit = () => {
    if (val.trim()) onConfirm(val.trim());
  };

  return (
    <div className="md-inline">
      <input
        ref={ref}
        className="text-input grow"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") onCancel();
        }}
        placeholder={placeholder}
      />
      {extra}
      <button type="button" className="btn btn-primary" style={{ padding: "7px 13px", fontSize: 12.5 }} onClick={submit}>
        Save
      </button>
      <button type="button" className="btn btn-secondary" style={{ padding: "7px 13px", fontSize: 12.5 }} onClick={onCancel}>
        Cancel
      </button>
    </div>
  );
}

function TaxonomyPanel({
  tone = "primary",
  icon,
  title,
  subtitle,
  items,
  globalQuery,
  onAdd,
  onRemove,
  addExtra,
}) {
  const [search, setSearch] = useState("");
  const [adding, setAdding] = useState(false);
  const query = (search || globalQuery).trim().toLowerCase();
  const visible = items.filter((item) => item.name.toLowerCase().includes(query));

  return (
    <div className="panel">
      <div className="taxo-head">
        <div style={{ display: "flex", alignItems: "center", gap: 11 }}>
          <div className="stat-ic" style={{ ...TONE_STYLE[tone], width: 36, height: 36 }}>
            {icon}
          </div>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14.5 }}>{title}</div>
            <div style={{ fontSize: 12, color: "var(--ink-faint)" }}>{subtitle}</div>
          </div>
        </div>
        <div className="btn-row">
          <span className={`chip chip-${tone}`}>{items.length}</span>
          {!adding && (
            <button
              type="button"
              className="btn btn-secondary"
              style={{ padding: "7px 13px", fontSize: 12.5 }}
              onClick={() => setAdding(true)}
            >
              + Add
            </button>
          )}
        </div>
      </div>

      <input
        className="text-input taxo-search"
        placeholder={`Search ${title.toLowerCase()}…`}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
      />

      {adding && (
        <AddInline
          placeholder={`New ${title.toLowerCase().replace(/s$/, "")}`}
          onConfirm={async (name) => {
            await onAdd(name);
            setAdding(false);
          }}
          onCancel={() => setAdding(false)}
          extra={addExtra}
        />
      )}

      <div className="tag-cloud">
        {visible.length === 0 && !adding && (
          <p className="empty-note" style={{ width: "100%", margin: 0 }}>
            {items.length === 0
              ? `No ${title.toLowerCase()} yet — add the first one.`
              : "No matches for that search."}
          </p>
        )}
        {visible.map((item) => (
          <span key={item.id} className={`tag-pill chip-${tone}`}>
            {item.name}
            <button type="button" className="tag-x" onClick={() => onRemove(item)} aria-label={`Remove ${item.name}`}>
              ×
            </button>
          </span>
        ))}
      </div>
    </div>
  );
}

const ManageDataAdmin = () => {
  const [countries, setCountries] = useState([]);
  const [grades, setGrades] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [bookTypes, setBookTypes] = useState([]);
  const [languages, setLanguages] = useState([]);
  const [standards, setStandards] = useState([]);
  const [formats, setFormats] = useState([]);
  const [tags, setTags] = useState([]);
  const [globalQuery, setGlobalQuery] = useState("");
  const [toast, setToast] = useState("");
  const [langCountryId, setLangCountryId] = useState("");

  const notify = (message) => setToast(message);

  useEffect(() => {
    if (!toast) return undefined;
    const timer = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(timer);
  }, [toast]);

  const fetchAll = useCallback(async () => {
    const headers = auth();
    const [countriesRes, gradesRes, subjectsRes, bookTypesRes, languagesRes, standardsRes, formatsRes, tagsRes] =
      await Promise.all([
        axios.get("/countries", headers).catch(() => ({ data: [] })),
        axios.get("/grades", headers).catch(() => ({ data: [] })),
        axios.get("/subjects", headers).catch(() => ({ data: [] })),
        axios.get("/booktypes", headers).catch(() => ({ data: [] })),
        axios.get("/languages", headers).catch(() => ({ data: [] })),
        axios.get("/standards", headers).catch(() => ({ data: [] })),
        axios.get("/book-formats", headers).catch(() => ({ data: [] })),
        axios.get("/tags", headers).catch(() => ({ data: [] })),
      ]);

    const nextCountries = countriesRes.data || [];
    setCountries(nextCountries);
    setGrades(gradesRes.data || []);
    setSubjects(subjectsRes.data || []);
    setBookTypes(bookTypesRes.data || []);
    setLanguages(languagesRes.data || []);
    setStandards(standardsRes.data || []);
    setFormats(formatsRes.data || []);
    setTags(tagsRes.data || []);
    setLangCountryId((prev) => prev || nextCountries[0]?.country_id || "");
  }, []);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const removeItem = async (item, label, request) => {
    if (!window.confirm(`Remove "${item.name}" from ${label}?`)) return;
    await request();
    notify(`Removed "${item.name}"`);
    fetchAll();
  };

  const chipItems = {
    countries: countries.map((item) => ({ id: item.country_id, name: item.country_name })),
    grades: grades.map((item) => ({ id: item.grade_id, name: item.grade_level })),
    subjects: subjects.map((item) => ({ id: item.subject_id, name: item.subject_name })),
    bookTypes: bookTypes.map((item) => ({ id: item.book_type_id, name: item.book_type_title })),
    standards: standards.map((item) => ({ id: item.standard_id, name: item.standard_name })),
    languages: languages.map((item) => {
      const country = countries.find((c) => c.country_id === item.country_id);
      return {
        id: item.language_id,
        name: country ? `${item.language_name} · ${country.country_name}` : item.language_name,
      };
    }),
    formats: formats.map((item) => ({ id: item.format_id, name: item.format_name })),
    tags: tags.map((item) => ({ id: item.tag_id, name: item.tag_name })),
  };

  const stats = [
    { key: "countries", label: "Countries", count: countries.length, tone: "primary" },
    { key: "grades", label: "Grades", count: grades.length, tone: "mint" },
    { key: "subjects", label: "Subjects", count: subjects.length, tone: "peach" },
    { key: "bookTypes", label: "Book types", count: bookTypes.length, tone: "lilac" },
    { key: "standards", label: "Standards", count: standards.length, tone: "primary" },
    { key: "languages", label: "Languages", count: languages.length, tone: "mint" },
  ];

  const langCountrySelect = (
    <select
      className="text-input"
      value={langCountryId}
      onChange={(e) => setLangCountryId(e.target.value)}
      style={{ minWidth: 140 }}
    >
      {countries.map((c) => (
        <option key={c.country_id} value={c.country_id}>
          {c.country_name}
        </option>
      ))}
    </select>
  );

  return (
    <div className="manage-data-admin-container md-page">
      <div className="page-head">
        <div>
          <span className="eyebrow-link">Library catalogue</span>
          <h1 className="page-title">Manage Data</h1>
          <p className="page-desc">
            Keep countries, grades, subjects and related catalogues consistent across every book and every
            user&apos;s access.
          </p>
        </div>
        <input
          className="text-input"
          placeholder="Search every catalogue…"
          style={{ minWidth: 260 }}
          value={globalQuery}
          onChange={(e) => setGlobalQuery(e.target.value)}
        />
      </div>

      <div className="kpi-strip">
        {stats.map((stat) => (
          <div key={stat.label} className="kpi-mini">
            <div className="kpi-ic" style={TONE_STYLE[stat.tone]}>
              {ICONS[stat.key]}
            </div>
            <div className="kpi-body">
              <div className="kpi-lbl">{stat.label}</div>
              <div className="kpi-num">{stat.count}</div>
            </div>
          </div>
        ))}
      </div>

      <p style={{ fontSize: 12, color: "var(--ink-faint)", fontWeight: 600, marginBottom: 10 }}>
        TAXONOMIES — tag-style values used to classify books
      </p>

      <div className="subgrid">
        <TaxonomyPanel
          tone="primary"
          icon={ICONS.countries}
          title="Countries"
          subtitle="Regions used for books and languages"
          items={chipItems.countries}
          globalQuery={globalQuery}
          onAdd={async (name) => {
            await axios.post("/countries", { country_name: name }, auth());
            notify(`Added "${name}"`);
            fetchAll();
          }}
          onRemove={(item) =>
            removeItem(item, "countries", () => axios.delete(`/countries/${item.id}`, auth()))
          }
        />
        <TaxonomyPanel
          tone="mint"
          icon={ICONS.grades}
          title="Grades"
          subtitle="Grade levels and course volumes"
          items={chipItems.grades}
          globalQuery={globalQuery}
          onAdd={async (name) => {
            await axios.post("/grades", { grade_level: name }, auth());
            notify(`Added "${name}"`);
            fetchAll();
          }}
          onRemove={(item) =>
            removeItem(item, "grades", () => axios.delete(`/grades/${item.id}`, auth()))
          }
        />
      </div>

      <div className="subgrid">
        <TaxonomyPanel
          tone="peach"
          icon={ICONS.subjects}
          title="Subjects"
          subtitle="Subjects assigned to titles"
          items={chipItems.subjects}
          globalQuery={globalQuery}
          onAdd={async (name) => {
            await axios.post("/subjects", { subject_name: name }, auth());
            notify(`Added "${name}"`);
            fetchAll();
          }}
          onRemove={(item) =>
            removeItem(item, "subjects", () => axios.delete(`/subjects/${item.id}`, auth()))
          }
        />
        <TaxonomyPanel
          tone="lilac"
          icon={ICONS.bookTypes}
          title="Book types"
          subtitle="Student, teacher and practice books"
          items={chipItems.bookTypes}
          globalQuery={globalQuery}
          onAdd={async (name) => {
            await axios.post("/booktypes", { book_type_title: name }, auth());
            notify(`Added "${name}"`);
            fetchAll();
          }}
          onRemove={(item) =>
            removeItem(item, "book types", () => axios.delete(`/booktypes/${item.id}`, auth()))
          }
        />
      </div>

      <div className="subgrid">
        <TaxonomyPanel
          tone="primary"
          icon={ICONS.standards}
          title="Standards"
          subtitle="Curriculum standards for cataloguing"
          items={chipItems.standards}
          globalQuery={globalQuery}
          onAdd={async (name) => {
            await axios.post("/standards", { standard_name: name }, auth());
            notify(`Added "${name}"`);
            fetchAll();
          }}
          onRemove={(item) =>
            removeItem(item, "standards", () => axios.delete(`/standards/${item.id}`, auth()))
          }
        />
        <TaxonomyPanel
          tone="mint"
          icon={ICONS.languages}
          title="Languages"
          subtitle="Languages mapped to a country"
          items={chipItems.languages}
          globalQuery={globalQuery}
          addExtra={langCountrySelect}
          onAdd={async (name) => {
            await axios.post(
              "/languages",
              { language_name: name, country_id: langCountryId || countries[0]?.country_id },
              auth()
            );
            notify(`Added "${name}"`);
            fetchAll();
          }}
          onRemove={(item) =>
            removeItem(item, "languages", () => axios.delete(`/languages/${item.id}`, auth()))
          }
        />
      </div>

      <div className="subgrid">
        <TaxonomyPanel
          tone="peach"
          icon={ICONS.formats}
          title="Book formats"
          subtitle="How a book is delivered"
          items={chipItems.formats}
          globalQuery={globalQuery}
          onAdd={async (name) => {
            await axios.post("/book-formats", { format_name: name }, auth());
            notify(`Added "${name}"`);
            fetchAll();
          }}
          onRemove={(item) =>
            removeItem(item, "formats", () => axios.delete(`/book-formats/${item.id}`, auth()))
          }
        />
        <TaxonomyPanel
          tone="lilac"
          icon={ICONS.tags}
          title="Tags"
          subtitle="Free-form labels for discovery"
          items={chipItems.tags}
          globalQuery={globalQuery}
          onAdd={async (name) => {
            await axios.post("/tags", { tag_name: name }, auth());
            notify(`Added "${name}"`);
            fetchAll();
          }}
          onRemove={(item) => removeItem(item, "tags", () => axios.delete(`/tags/${item.id}`, auth()))}
        />
      </div>

      {toast && <div className="md-toast">{toast}</div>}
    </div>
  );
};

export default ManageDataAdmin;
